import { isTrue, noop } from "utils";

/**
 * For a high-level sequence diagram of how our PayPal flow works
 * see this link: https://mermaid.live/edit#pako:eNrVVk2P4jgQ_SulaCVA4uOOVi21enZWvVrNRNOwXPpSJEWwMHbGdppGiP--ZVcSmqF7tNKcNidwfb16r1zJKStsSdk88_S9IVPQJ4WVw_2zAX6wCNbBEtDD0pOTwxpdUIWq0QRYRdNKlRUFWD7e2hfRvlBa4X2tbs15Hu05HnPUYl1OJnd3qzk8aFXsPAzuy_L3tZvdic8A1k0I1ojvil0Xc_iTa3tVGSrhQGsIdkcmxRxU2ELDuMEHDATKbKxELqTINwqNM_DXaiFRN2lrPNaowVGpHBXc4re_U-YhJ3S9eaPtYZTOJ44q5QM5f4lpnPYQoSSPNmTom6Ig72cFMud69C5eZqoT4hrwD7AkeEavteOUUGyp2NlGjq_64iR5Pk9SgrpAZOKCvVGh94zceiZvnBIW1hiO8RHEnljDPYWtLdt50UEakLZ8i1SM8cnzSdeJ1Pax9EEGiJkSdmeNmknkFT8duYxXMQmOXpRtPJNF9TQ5PFgTUDHYd0V_o-0_5NRGUaQgEsoJeQYufEFCgLWa8UClk9lLjDiOLpmuFZEwcRIf7p46oVqxf4GGNsP_kIf4fLEMwPIpsOfyEbjLbbwj3u4pzg-mkiUGBFzz5AKbBfvwMR8DhWJ0A_2zMspvoXAkVyURzVB59HTH3zV5s99OcrCIIM8f9nClFpnyze1hp3bZuV64NRa7qJ51qlIGg7JmLssA1AY2zu47YX3hVB3GLEV4CK9RO5HGuuQf1wgrt1HVtL1keYL7YPe1pkBLp78yh06VlPw5u2_qWiuSFTmJ5Rqta6xonJYSPH7iErYdFM9NMTgu3W-bDsLl9nKOfnPdYodh3aw1887SHBzWtTIVxHqjD8S-B36ruKOsMhg-ZyG-CyZSNBvFLYRlKQuIqQa7YWzA48cD0Jca-Hgn0g4FhBfUDYFWOxLXU-z0PD3FnI3nH2iOQK_BYZr585T13aq4gtNccESVNrvzQgvP2VPPDAyZAHxBpXGtaQRoSonxHGNNGsxBLPgUOxgImKm4LNjWm1qYvL5ijAGZGUwVUfyTQtx3aSEqUfnUVLzPE086CoAcUvCO1UdhSeLeW7xp2yTt-JV9UXDIo0bOoP5InwteZqip-QomUiSIy65pw-MjdbusidCft9oFtP3Cf2i1C3nb7w_vmGl_JbNxtie3R1Xyl8spHvNkbWnPMzXnnyW63XP2bM7sJ039USq-B9k8uIbGGTbBPh1N0f0Xn_bLJ5tvkNk8_wul3RdC
 * mermaid.live markup:
 sequenceDiagram
    actor U as User
    participant W as Widget UI
    participant T as TiliaApi
    participant PP as PayPal
    U-->>W: Clicks 'Add<br/>PayPal' button
    W->>T: Get signed web token<br/>with user state info
    T->>W: Return JWT token
    W->>T: Get paypal redirect URL<br/>(start paypal flow)<br/>-registers redirect urls with<br/>paypal (success/cancel)<br/>with user state param
    T->>W: Return paypal redirect<br/>w/express checkout<br/>token
    W-->>PP: User is redirected to PayPal
    U-->PP: User signs in,<br/>connects payment method
    alt user cancels paypal
        PP-->>W: Redirects to widget url<br/>(/ui/paypal/cancel)<br/>registered in previous step.<br/>Contains user state info
        W->>T: Verifies returned JWT<br/>token (/ui/api/webtoken/verify)
        T->>W: Return token verify
    else paypal success
        PP-->>W: Redirects to widget url<br/>(/ui/paypal/success)<br/>registered in previous step.<br/>Contains user state info
        W->>T: Verifies returned JWT<br/>token (/ui/api/webtoken/verify)
        T->>W: Return token verify
        Note over W: UI gethers some meta<br/>data about the user (IP, etc)
        W->>T: Finish create paypal api call<br/>(/paypal/success/${paypalToken})
        T->>W: Return success
    end
    W-->>W: Widget redirects back to origination:<br/>- if from widget script, intCtx in state or<br/>flowConfig.postOAuthURLOverride<br/>if supplied<br/>-if fullpage, flow ID stored in sessionCtx<br/>within state
    alt if redirect from widget script (publisher wrapping page)
        Note over W: A query param ("tilia-state") is added to end of<br />the publisher's url with a value like<br />{flow}.{status}.{any extra info}.  This will<br />get parsed in useSessionCtx (if available) and<br />set on the 'flowState' value.<br />The flowState value can then get read in a<br />flow to do things like pre-select a recently added<br />payment method
    else if react redirect (internal)
        Note over W: flowState is updated internally before<br />redirect.  The flowState value can then get<br />read in a flow to do things like pre-select a<br />recently added payment method.
    end
*/

export const FLOW_STATE = {
  CANCEL: "paypal.cancel",
  COMPLETE: "paypal.complete",
  ERROR: "paypal.error",
};

// returns true if the add paypal button should be displayed
export const offerPaypal = (config = {}, flowConfig = {}, debug = noop) => {
  const configAllowExclude = isTrue(config.allowExcludePaypal);
  const requestExclude = isTrue(flowConfig.excludePaypal);
  if (requestExclude && !configAllowExclude) {
    debug("!!!exclude paypal requested but not configured in settings!!!");
  }
  const excludePaypal = configAllowExclude && requestExclude;

  const configAdd = isTrue(config.addPaypal);
  return configAdd && !excludePaypal;
};

export { AddPaypalRedirect } from "./Redirect";
export { parseToken, AddPaypalButton } from "./Button";
export { Fraudnet, shouldInvokeFraudnet } from "./Fraudnet";
